import React from 'react';

import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { getPdpHeaderBackgroundColor } from 'features/app-takeover/utils/pdp-header-background-color';
import { ProductDetailBackArrow } from 'features/menu/components/product-detail/product-detail-flow/product-detail-back-arrow';
import { ProductDetailClose } from 'features/menu/components/product-detail/product-detail-flow/product-detail-close';
import {
  Container,
  ProductDetailFlowHeaderContainer,
  ProductTitle,
} from 'features/menu/components/product-detail/product-detail-flow/product-detail-header/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { ProductWizardStep } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { StyleSheet } from 'react-native';

import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { primitive } from 'styles/constants';

interface IProductDetailHeader {
  eventAttributes?: { [key: string]: string };

  /**
   * Used to be optional and had logic to figure out the title based on context, now each component is responsible for passing the title
   * You can figure out which title to pass by looking at the previous implementation here:
   * src/components/product-detail-v2/product-detail-flow/product-detail-header/use-product-detail-header.ts
   */
  title: string;
}

const HEADER_FLAIR_HEIGHT = 64;
const HEADER_FLAIR_OVERLAP = 2;
const HEADER_WITH_FLAIR_HEIGHT = HEADER_FLAIR_HEIGHT + HEADER_FLAIR_OVERLAP;

const PRODUCT_CUSTOMIZATION_STEPS: ReadonlySet<ProductWizardStep> = new Set([
  ProductWizardStep.ProductCustomization,
  ProductWizardStep.PickerSelection,
  ProductWizardStep.PickerCustomization,
  ProductWizardStep.PickerCustomizationSides,
  ProductWizardStep.PickerCustomizationDrinks,
]);

export const ProductDetailHeader: React.FC<IProductDetailHeader> = ({ title, eventAttributes }) => {
  const { currentFlowStep, cartIdEditing, goToPreviousStep, id } = useProductWizardFlow();
  const {
    headerBackgroundColor: takeoverHeaderBg,
    headerCustomizationBackgroundColor: takeoverCustomizationHeaderBg,
    headerReviewBackgroundColor: takeoverReviewHeaderBg,
    fontFamily: takeoverFontFamily,
    headerFontColor: takeoverHeaderFontColor,
    HeaderFlair,
  } = useAppTakeoverPdp(id);

  const { setShowCloseDialog } = useProductWizardContext();

  const showCloseButton = currentFlowStep.step !== ProductWizardStep.ProductCustomization;
  const showBackArrow = currentFlowStep.step !== ProductWizardStep.ProductHome;

  const handleClose = () => {
    logButtonClick({
      attributes: {
        ...eventAttributes,
        name: eventAttributes?.name ?? '',
        component: ClickEventComponentNames.PRODUCT_DETAIL_CLOSE,
      },
    });

    if (currentFlowStep.step === ProductWizardStep.ProductHome || cartIdEditing) {
      return goToPreviousStep();
    }
    setShowCloseDialog(true);
  };

  const currentStep = currentFlowStep.step;
  const isProductCustomization = PRODUCT_CUSTOMIZATION_STEPS.has(currentStep);
  const isProductReview = currentStep === ProductWizardStep.PickerReview;
  const resolvedTakeoverHeaderBg = getPdpHeaderBackgroundColor(
    {
      headerBackgroundColor: takeoverHeaderBg,
      headerCustomizationBackgroundColor: takeoverCustomizationHeaderBg,
      headerReviewBackgroundColor: takeoverReviewHeaderBg,
    },
    { isProductCustomization, isProductReview }
  );

  return (
    <ProductDetailFlowHeaderContainer
      testID="product-detail-flow-header"
      style={HeaderFlair ? styles.headerWithContinuousArtwork : undefined}
      backgroundColor={
        resolvedTakeoverHeaderBg ||
        (currentStep === ProductWizardStep.ProductHome ? primitive.$mayoEggWhite : primitive.$white)
      }
    >
      {HeaderFlair && (
        <HeaderFlair
          isProductCustomization={isProductCustomization}
          isProductReview={isProductReview}
        />
      )}
      <Container>
        {showBackArrow && (
          <ProductDetailBackArrow
            onPress={goToPreviousStep}
            eventAttributes={eventAttributes}
            color={takeoverHeaderFontColor}
          />
        )}
        {showCloseButton && (
          <ProductDetailClose onDismiss={handleClose} color={takeoverHeaderFontColor} />
        )}
        <ProductTitle fontFamily={takeoverFontFamily} color={takeoverHeaderFontColor}>
          {title}
        </ProductTitle>
      </Container>
    </ProductDetailFlowHeaderContainer>
  );
};

const styles = StyleSheet.create({
  headerWithContinuousArtwork: {
    height: HEADER_WITH_FLAIR_HEIGHT,
    marginBottom: -HEADER_FLAIR_OVERLAP,
    paddingBottom: HEADER_FLAIR_OVERLAP,
    position: 'relative',
    zIndex: 1,
  },
});
